import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

const STATUS_LABEL = { open: 'Aperta', in_review: 'In revisione', resolved: 'Risolta', archived: 'Archiviata' };
const STATUS_TONE = { open: 'bg-terracotta/20 text-terracotta', in_review: 'bg-secondary', resolved: 'bg-accent text-accent-foreground', archived: 'bg-muted text-muted-foreground' };

export default function AdminReports() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') || '';
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [newStatus, setNewStatus] = useState('in_review');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    const { data } = await api.get('/admin/reports', { params: { status: status || undefined } });
    setItems(data || []);
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const doUpdate = async () => {
    try {
      await api.post(`/admin/reports/${dialog.report_id}/status`, { status: newStatus, note });
      toast.success('Segnalazione aggiornata');
      setDialog(null); setNote(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Segnalazioni</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Moderazione contenuti</h1>
        <p className="mt-2 text-sm text-muted-foreground">Ogni cambio di stato viene registrato nell'Audit Log.</p>
      </header>

      <div className="flex gap-3">
        <Select value={status || 'all'} onValueChange={v=>setSp(v==='all'?{}:{status:v})}>
          <SelectTrigger className="w-[200px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="open">Aperte</SelectItem>
            <SelectItem value="in_review">In revisione</SelectItem>
            <SelectItem value="resolved">Risolte</SelectItem>
            <SelectItem value="archived">Archiviate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {items.length===0 ? <div className="p-10 text-sm text-muted-foreground text-center">Nessuna segnalazione</div> :
          <ul className="divide-y divide-border">
            {items.map(r => (
              <li key={r.report_id} className="p-5" data-testid={`admin-report-${r.report_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[r.status]||'bg-secondary'}`}>{STATUS_LABEL[r.status]||r.status}</span>
                    <span className="text-muted-foreground">{r.target_type} · {new Date(r.created_at).toLocaleString('it-IT')}</span>
                  </div>
                  <div className="flex gap-2">
                    {r.target_type === 'listing' && <Link to={`/terreno/${r.target_id}`} target="_blank" className="text-xs link-underline flex items-center gap-1">Apri terreno <ExternalLink size={11}/></Link>}
                    <Button size="sm" variant="outline" onClick={()=>{ setNewStatus(r.status==='open'?'in_review':'resolved'); setDialog(r); }}>Aggiorna stato</Button>
                  </div>
                </div>
                <div className="mt-3 text-sm"><b>Motivo:</b> {r.reason}</div>
                {r.details && <div className="mt-1 text-sm text-muted-foreground">{r.details}</div>}
                {r.reporter && <div className="mt-2 text-xs text-muted-foreground">Segnalante: {r.reporter.name} ({r.reporter.email})</div>}
                {r.moderator_note && <div className="mt-2 rounded-lg bg-secondary p-2 text-xs">Nota moderatore: {r.moderator_note}</div>}
              </li>
            ))}
          </ul>}
      </div>

      <Dialog open={!!dialog} onOpenChange={o=>{ if(!o){setDialog(null); setNote('');} }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aggiorna stato segnalazione</DialogTitle></DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Aperta</SelectItem>
              <SelectItem value="in_review">In revisione</SelectItem>
              <SelectItem value="resolved">Risolta</SelectItem>
              <SelectItem value="archived">Archiviata</SelectItem>
            </SelectContent>
          </Select>
          <Textarea rows={3} placeholder="Nota per audit log" value={note} onChange={e=>setNote(e.target.value)}/>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>{setDialog(null); setNote('');}}>Annulla</Button>
            <Button onClick={doUpdate}>Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
